/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  ReactElement,
  useEffect,
  useReducer,
  useCallback,
  useRef
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fulFillmentDocActions,
  checkoutActions,
  userActions
} from '@detox/actions';
import { Form } from '@wec-core/form-engine';
import useTranslate from '../../../hooks/useTranslation';
import {
  getFormDataMapping,
  getNRIC,
  getStaffPassUploadGroup,
  getBillingGroup,
  getSubmitGroup,
  getValidationSchema,
  documentTypeValidation,
  documentSizeValidation,
  FORM_MAPPING_ACTIONS,
  getFormConfigForNewNewFlow,
  checkNricSilver,
  fileUploaded,
  uploadedFileDeleted,
  getItemSkuToOrderIdMapping,
  updateCustomer
} from '../helper';
import {
  TObject,
  TFile,
  BillingPrefType,
  ErrorScenarioType,
  UploadedDocument,
  FormConfig,
  DeliveryState
} from '../../../types/registrationCheckout';
import {
  checkoutFlowReducer as newNewFlowReducer,
  checkoutInitialState as newNewFlowInitialState
} from '../checkoutFlowState';
import { navigation } from '../../../middlewares/navigation-constants';
import { getCartError, getCartErrorDataModel } from '../../../helpers/cart';
import { KeyValue } from '../../../types/common.types';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import isFeatureFlagEnabled from '../../../helpers/feature-flags';
import { FEATURE_FLAG_ALLOW_SUBSEQUENT_ALLOCATE_API } from '../../../types/featureFlag.types';
import { useMcssVerifyEkyc } from '../../../hooks/useEkycEnhancements';
import ORDER_CONSTANT from '../../../constants/order';
import isSimOnlyPlan from '../../../helpers/is-sim-only-plan';
import { useLoader } from '../../../hooks/useLoader';
import { useProcessFormFields } from '../../../hooks/useProcessFormFields';
import { AbFeatureNames } from '../../../constants/ab-feature-names';
import { useABTesting } from '@wec-core/ab-testing';

interface TProps {
  enableReinitialize?: boolean;
  stackSpacing: number;
  isCisFlow: KeyValue;
  prefilledData?: KeyValue;
  idCardLinkClicked?: () => void;
  proofLinkClicked?: () => void;
  kycLinkClicked?: () => void;
  onValuesChange?: (values: TObject) => void;
  onFormSubmit?: (values: TObject, navigateTo: string) => void;
  billingPrefData: BillingPrefType[];
  errorScenariosData: ErrorScenarioType[];
  onErrorScenarioFound?: (values: TObject) => void;
  handleCheckoutHistoryState?: (needPushState?: boolean) => void;
  goToOrderSummary: () => void;
  isEshopCisDocEnabled?: boolean;
  onGettingFormConfigs: (formConfigs: FormConfig) => void;
  showStepper?: () => void;
}

const NewNewFlow: React.FC<TProps> = ({
  isCisFlow,
  idCardLinkClicked,
  proofLinkClicked,
  kycLinkClicked,
  onValuesChange,
  onFormSubmit,
  billingPrefData,
  errorScenariosData,
  onErrorScenarioFound,
  handleCheckoutHistoryState,
  goToOrderSummary,
  onGettingFormConfigs,
  showStepper,
  prefilledData,
  isEshopCisDocEnabled,
  ...props
}): ReactElement => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const { uploadDocument, deleteUploadedDocument } = fulFillmentDocActions;

  const {
    verificationData,
    checkoutFormDocumentData,
    productOrderReferenceNumber,
    documentUpload,
    device,
    order,
    selectedPlan,
    selectedProduct,
    isLoading,
    checkout,
    cart,
    ekycState,
    userInformation,
    delivery,
    user
  } = useSelector((state: TObject) => ({
    verificationData: state.checkout?.verificationData,
    checkoutFormDocumentData: state.checkout?.checkoutFormDocumentData,
    documentData: state.checkout?.documentData,
    productOrderReferenceNumber:
      state.order?.productOrder?.productOrderReferenceNumber,
    order: state.order,
    documentUpload: state.checkout?.documentUpload as UploadedDocument[],
    device: state.cart?.cartOrder?.device,
    selectedProduct: state.product?.selectedProduct,
    selectedPlan: state.plan?.selectedPlan,
    isLoading: state.checkout?.isLoading,
    checkout: state.checkout,
    cart: state.cart,
    ekycState: state.ekyc,
    userInformation: state.user?.information,
    delivery: state.fulfillment?.delivery as DeliveryState,
    user: state.user
  }));
  const { productOrder, addedToContact } = order;
  const [formState, formDispatch] = useReducer(
    newNewFlowReducer,
    newNewFlowInitialState
  );

  useMcssVerifyEkyc();
  const preStaff = checkoutFormDocumentData['staffPassUpload'];
  const nric = verificationData?.passTypeText
    ? null
    : getNRIC(t, verificationData);
  const staffPassUploadGroup = getStaffPassUploadGroup(t, !!preStaff);
  const billingPrefGroup = getBillingGroup(t, billingPrefData);
  const formData = {};
  const isNricSilverRef = useRef<boolean>(false);
  const hasDocumentErrors = useRef<TObject>({});
  const hasAccessories = cart?.cartOrder?.accessories?.length > 0;
  const simType = cart?.order?.newlyAddedSimDetails?.simDetails?.simType;
  const { renderError } = useErrorHandler({
    states: [checkout]
  });
  const verifyEkycMcssPayload = ekycState?.verifyEkycPayload || {};

  const getFlowConfigs = (isNricSilver = false) => {
    return getFormConfigForNewNewFlow({
      device: device,
      isCis: !!isCisFlow && !isCisFlow.$myInfo,
      isNric: nric,
      isNricSilver,
      hasAccessories,
      simType
    });
  };

  const getNonPersonalGroup = (isNricSilver = false) => {
    const flowConfigs = getFlowConfigs(isNricSilver);

    const buttonText = flowConfigs.byPassFulfilment
      ? t('CO_NN_PROCEED_ORDER_SUMMARY_BTN')
      : t('CO_NN_PROCEED_BTN');
    const submitGroup = getSubmitGroup(t, false, false, buttonText);

    return {
      group: {
        staffPass: flowConfigs.showStaffUploadDocument
          ? staffPassUploadGroup
          : null,
        billingPref: flowConfigs.showBillingPref ? billingPrefGroup : null,
        submit: submitGroup
      },
      groupConfig: {
        showBillingPref: flowConfigs.showBillingPref,
        showNricFIN: flowConfigs.showNricFIN,
        showStaffUploadDocument: flowConfigs.showStaffUploadDocument,
        showExistingBillingAddress: flowConfigs.showExistingBillingAddress,
        byPassFulfilment: flowConfigs.byPassFulfilment
      }
    };
  };

  // initial config for the form
  const formFieldsData = {
    staffPass: null,
    persSectionDivider: null,
    billingPref: null,
    submit: null
  };

  const setOverAllFormState = useCallback((): void => {
    const { group, groupConfig } = getNonPersonalGroup();

    if (typeof onGettingFormConfigs === 'function') {
      onGettingFormConfigs({
        hasBillingPref: groupConfig.showBillingPref,
        hasUploadID: groupConfig.showStaffUploadDocument,
        byPassFulfilment: groupConfig.byPassFulfilment
      });
    }

    const payload = {
      formInputsMapping: { ...group },
      formValues: { ...formFieldsData }
    };

    formDispatch({
      type: FORM_MAPPING_ACTIONS.SET_OVERALL_FORM_STATE,
      payload
    });
  }, []);

  const uploadFile = (
    fieldName,
    fieldsGroupName,
    docGroup,
    uploadedFile,
    isEshopDocEnabled?: boolean
  ) => {
    return fileUploaded({
      uploadedFile,
      fieldName,
      fieldsGroupName,
      docGroup,
      hasDocumentErrors,
      productOrderReferenceNumber,
      uploadDocument,
      dispatch,
      formDispatch,
      t,
      isEshopDocEnabled
    });
  };

  const deleteFile = (
    fileName,
    fieldName,
    fieldsGroupName,
    docGroup,
    isEshopDocEnabled?: boolean
  ) => {
    return uploadedFileDeleted({
      fileName,
      fieldName,
      fieldsGroupName,
      docGroup,
      productOrderReferenceNumber,
      documentUpload,
      dispatch,
      formDispatch,
      deleteUploadedDocument,
      isEshopDocEnabled
    });
  };

  const callbacks = () => {
    const staffGroup = formState.formInputsMapping['staffPass'];
    const proofGroup = formState.formInputsMapping['proof'];
    const kycGroup = formState.formInputsMapping['kyc'];

    return {
      idCardLinkClicked: idCardLinkClicked,
      proofLinkClicked: proofLinkClicked,
      kycLinkClicked: kycLinkClicked,
      documentTypeValidation: (file: File) => documentTypeValidation(file, t),
      documentSizeValidation: (file: File) => documentSizeValidation(file, t),
      staffPassDropped: (file: TFile) =>
        uploadFile(
          'staffPassUpload',
          'staffPass',
          staffGroup,
          file,
          isEshopCisDocEnabled
        ),
      staffPassDeleted: fileName =>
        deleteFile(
          fileName,
          'staffPassUpload',
          'staffPass',
          staffGroup,
          isEshopCisDocEnabled
        ),
      proofDropped: (file: TFile) =>
        uploadFile('proofUpload', 'proof', proofGroup, file),
      proofDeleted: fileName =>
        deleteFile(fileName, 'proofUpload', 'proof', proofGroup),
      kycDropped: (file: TFile) =>
        uploadFile('kycUpload', 'kyc', kycGroup, file),
      kycDeleted: fileName => deleteFile(fileName, 'kycUpload', 'kyc', kycGroup)
    };
  };

  const { isFeatureAllowed: abTestDeletePendingOrderForNewFlow } = useABTesting(
    {
      featureName: AbFeatureNames.DELETE_PENDING_ORDER_FOR_NEW_FLOW
    }
  );

  // sequence of useEffect is important to update form preloaded
  useEffect(() => {
    const flowConfigs = getFlowConfigs();
    if (onGettingFormConfigs) {
      const needStaffUpload = flowConfigs.showStaffUploadDocument;
      onGettingFormConfigs({
        hasBillingPref:
          flowConfigs.showBillingPref || flowConfigs.showExistingBillingAddress,
        hasUploadID: needStaffUpload,
        byPassFulfilment: flowConfigs.byPassFulfilment
      });
    }
    if (handleCheckoutHistoryState) {
      handleCheckoutHistoryState();
    }
    dispatch(checkoutActions.resetCreateCustomerStatus());
  }, []);

  useEffect(() => {
    setOverAllFormState();
  }, [setOverAllFormState]);

  useEffect(() => {
    if (verifyEkycMcssPayload?.contactExists) {
      dispatch(userActions.getUserInformation());
    }
  }, [verifyEkycMcssPayload?.contactExists]);

  useEffect(() => {
    if (
      verifyEkycMcssPayload?.contactExists &&
      userInformation?.anonymousContactId
    ) {
      dispatch(
        checkoutActions.retrieveContactDetails(
          userInformation?.anonymousContactId
        )
      );
    }
  }, [
    verifyEkycMcssPayload?.contactExists,
    userInformation?.anonymousContactId
  ]);

  useEffect(() => {
    if (checkout?.customerCreated) {
      const hasError =
        cart?.checkoutErrorData &&
        cart?.checkoutOrderError &&
        cart?.checkoutErrorScenarioCheckCompleted;
      const finishedWithoutError =
        cart?.checkoutErrorScenarioCheckCompleted &&
        cart?.checkoutOrderError === false;
      if (hasError) {
        showValidateError();
      } else if (finishedWithoutError) {
        onCustomerCreated();
      }
    }
  }, [
    checkout?.customerCreated,
    cart?.checkoutErrorData,
    cart?.checkoutOrderError,
    cart?.checkoutErrorScenarioCheckCompleted
  ]);

  const showValidateError = () => {
    const errorScenarioData = getCartError(
      cart?.checkoutErrorData,
      errorScenariosData
    );
    const cartErrorDataModel = getCartErrorDataModel(
      errorScenarioData,
      selectedPlan?.planName
    );
    onErrorScenarioFound(cartErrorDataModel);
  };

  const onCustomerCreated = async () => {
    if (isFeatureFlagEnabled(FEATURE_FLAG_ALLOW_SUBSEQUENT_ALLOCATE_API)) {
      await checkoutActions.allocateEquipmentForAvailableProducts({
        cart,
        simOptions: productOrder?.sim,
        dispatch,
        productOrderId: productOrder?.productOrderId,
        notAvailableSkus: [],
        itemSkuToOrderIdMapping: getItemSkuToOrderIdMapping(
          selectedProduct?.deviceCode,
          productOrder?.productOrderItemId
        )
      });
    }
    dispatch(checkoutActions.resetCreateCustomerStatus());

    const flowConfig = getFlowConfigs();
    if (flowConfig.byPassFulfilment) {
      return goToOrderSummary();
    }

    onFormSubmit({}, `/${navigation.ORDER_FULFILMENT_PAGE}`);
  };

  const formattedFormFieldsData = getFormDataMapping(
    formState.formInputsMapping
  );

  const { processedFormFieldsData } = useProcessFormFields({
    formFieldsData: formattedFormFieldsData
  });

  const _onValueChanged = (values, _, getFieldStatus): void => {
    const dob = values?.birthday;
    if (dob && nric && getFieldStatus('birthday') && values.postalCode) {
      const isNricSilver = checkNricSilver(dob, !!nric);
      // only dispatch to change formInputsMapping when isNricSilver change value with previous one
      if (
        (isNricSilver && !isNricSilverRef.current) ||
        (!isNricSilver && isNricSilverRef.current)
      ) {
        formDispatch({
          type: FORM_MAPPING_ACTIONS.SET_FORM_INPUT_MAPPING,
          payload: {
            ...getNonPersonalGroup(isNricSilver)
          }
        });
      }
      isNricSilverRef.current = isNricSilver;
    }

    onValuesChange(values);
  };

  const _onValidate = <TValuesObj,>(_: TValuesObj): TValuesObj => {
    let errorState = {};
    errorState = { ...errorState, ...hasDocumentErrors.current };
    return errorState as TValuesObj;
  };

  const _onFormSubmit = async values => {
    const retrieveContactData = checkout?.checkoutRetrieveContactData;
    const params = {
      formValues: values,
      addressInfo: retrieveContactData?.address,
      passType: retrieveContactData?.documentType,
      passId: retrieveContactData?.documentId,
      addedToContact,
      portIn: productOrder?.type === ORDER_CONSTANT.TYPE.PORTIN,
      productOrder,
      isSimOnlyIPP: isSimOnlyPlan(selectedPlan) && selectedProduct,
      dispatch
    };
    if (
      verifyEkycMcssPayload?.customerExists &&
      userInformation?.anonymousContactId
    ) {
      return await updateCustomer({
        ...params,
        contactId: userInformation?.anonymousContactId,
        customerId: userInformation?.anonymousCustomerId,
        selectedPlan,
        billingPrefData,
        retrieveContactData,
        shouldDeleteOpenOrder:
          order.productOrder?.type === ORDER_CONSTANT.TYPE.NEW &&
          abTestDeletePendingOrderForNewFlow
      });
    }
  };

  useEffect(() => {
    if (formattedFormFieldsData?.length) {
      showStepper();
    }
  }, [formattedFormFieldsData]);

  const { Loader } = useLoader({
    states: [checkout, delivery, user],
    loadings: [isLoading, delivery?.isDeliveryLoading]
  });

  return processedFormFieldsData && processedFormFieldsData.length > 0 ? (
    <>
      <Form
        {...props}
        enableReinitialize={formState.enableReinitialize}
        formFieldsConfig={processedFormFieldsData}
        initialValues={formState.formValues}
        data={formData}
        callbacks={callbacks()}
        onValueChange={_onValueChanged}
        onSubmit={_onFormSubmit}
        onValidate={_onValidate}
        validationSchema={getValidationSchema(t)}
        valuesChangeOnDemand={formState.valuesChangeOnDemand}
        enableInputErrorFocus
      />
      {renderError()}
      <Loader />
    </>
  ) : null;
};

export default NewNewFlow;
